/**
 * frontend/src/views/LoginView.ts
 * =================================
 * Pantalla de ingreso.
 *
 * Es lo primero que ve un piloto y lo único que se muestra mientras no haya
 * sesión. No reutiliza el modal de preferencias ni el asistente: una pantalla
 * completa deja claro que no hay nada más que hacer hasta ingresar.
 *
 * No lleva título ni texto de bienvenida. Dos campos, uno con una etiqueta que
 * dice «Contraseña», debajo de la marca, ya dicen qué es esto: rotularlo
 * además «Acceso», «Ingrese a ConciliA» y «use el usuario que recibió» es
 * repetir tres veces lo mismo y empujar los campos hacia abajo. El encabezado
 * existe igual para los lectores de pantalla, que no ven la forma.
 *
 * Decisiones que parecen detalles y no lo son:
 *   - El formulario es un <form> de verdad: Enter envía, el navegador ofrece
 *     guardar la contraseña y los lectores de pantalla lo anuncian como tal.
 *   - El error se anuncia por `aria-live`: quien no ve la pantalla igual se
 *     entera de que la credencial no sirvió.
 *   - El botón se bloquea mientras el servidor responde. Verificar una
 *     contraseña tarda a propósito, y sin esto se acumulan cinco intentos.
 */

import { ApiError, ingresar } from "../api";
import { escapeHtml as _esc } from "../utils/texto";
import { icon } from "../utils/icons";
import logoUrl from "../assets/logo.png";

export function renderLoginView(contenedor: HTMLElement, alIngresar: (usuario: string) => void): void {
  contenedor.innerHTML = "";

  const vista = document.createElement("div");
  vista.className = "login-view";
  vista.innerHTML = `
    <form class="login-card" id="login-form" novalidate>
      <img class="login-card__logo" src="${logoUrl}" alt="ConciliA" width="160" height="60" />
      <h1 class="solo-lectores">Ingreso</h1>

      <label class="login-card__campo">
        <span>Usuario</span>
        <input id="login-usuario" name="usuario" type="text" autocomplete="username"
               autocapitalize="none" spellcheck="false" required />
      </label>

      <label class="login-card__campo">
        <span>Contraseña</span>
        <input id="login-clave" name="password" type="password" autocomplete="current-password" required />
      </label>

      <p class="login-card__error" id="login-error" role="alert" aria-live="polite" hidden></p>

      <button class="btn btn--primary btn--large login-card__enviar" id="login-enviar" type="submit">
        ${icon("arrow")} <span>Ingresar</span>
      </button>

      <p class="login-card__pie">
        ${icon("shield")}
        Sus archivos se procesan en memoria y no se almacenan de forma permanente.
      </p>
    </form>
  `;

  contenedor.appendChild(vista);

  const form    = vista.querySelector<HTMLFormElement>("#login-form")!;
  const usuario = vista.querySelector<HTMLInputElement>("#login-usuario")!;
  const clave   = vista.querySelector<HTMLInputElement>("#login-clave")!;
  const boton   = vista.querySelector<HTMLButtonElement>("#login-enviar")!;
  const error   = vista.querySelector<HTMLParagraphElement>("#login-error")!;

  usuario.focus();

  const mostrarError = (mensaje: string): void => {
    error.innerHTML = `${icon("alert")} <span>${_esc(mensaje)}</span>`;
    error.hidden = false;
  };

  form.addEventListener("submit", async (evento) => {
    evento.preventDefault();
    error.hidden = true;

    if (!usuario.value.trim() || !clave.value) {
      mostrarError("Complete el usuario y la contraseña.");
      (usuario.value.trim() ? clave : usuario).focus();
      return;
    }

    boton.disabled = true;
    boton.querySelector("span")!.textContent = "Verificando…";

    try {
      const sesion = await ingresar(usuario.value.trim(), clave.value);
      alIngresar(sesion.usuario);
    } catch (err) {
      // La contraseña no se conserva en el campo tras un intento fallido: si
      // el error fue un carácter de más, se vuelve a escribir entera.
      clave.value = "";
      clave.focus();
      mostrarError(
        err instanceof ApiError ? err.message : "No se pudo verificar el ingreso."
      );
    } finally {
      boton.disabled = false;
      boton.querySelector("span")!.textContent = "Ingresar";
    }
  });
}
